import { useCallback, useEffect, useState } from "react";
import { useToast } from "@shared-kernel/primary/shared/shadcn/ui/use-toast";
import type { AppState } from "@redux/app-state";
import { useAppDispatch, useAppSelector } from "@redux/hooks";
import { resetInitializeContract } from "@academy-context/write/application/use-cases/external-provider/subcontracting-contract-enable/enable-subcontracting-contract";
import { createSelector } from "@reduxjs/toolkit";

const selectContractInitialization = createSelector(
  [(state: AppState) => state.externalProvider.subcontractingContractEnable],
  contractEnable => contractEnable,
);

export const useContractInitialization = () => {
  const dispatch = useAppDispatch();
  const { toast } = useToast();
  const { processing, signatureLink } = useAppSelector(selectContractInitialization);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [contractSignedOptimistically, setContractSignedOptimistically] = useState(false);

  useEffect(() => {
    if (processing === "success" && signatureLink) {
      setIsDialogOpen(true);
    } else if (processing === "failed") {
      toast({ description: "Erreur lors de l'initialisation du contrat", variant: "destructive" });
      dispatch(resetInitializeContract());
    }
  }, [dispatch, processing, signatureLink, toast]);

  const handleSignatureDone = useCallback(() => {
    setContractSignedOptimistically(true);
    toast({ description: "Le contrat a été signé avec succès." });
  }, [toast]);

  const handleCloseDialog = useCallback(() => {
    setIsDialogOpen(false);
    dispatch(resetInitializeContract());
  }, [dispatch]);

  const handleDeclined = useCallback(() => {
    setIsDialogOpen(false);
    toast({ description: "La signature du contrat a été déclinée." });
    dispatch(resetInitializeContract());
  }, [dispatch, toast]);

  const handleError = useCallback(() => {
    setIsDialogOpen(false);
    toast({ description: "Une erreur est survenue lors de la signature du contrat.", variant: "destructive" });
    dispatch(resetInitializeContract());
  }, [dispatch, toast]);

  return {
    processing,
    isDialogOpen,
    signatureLink,
    contractSignedOptimistically,
    handleSignatureDone,
    handleCloseDialog,
    handleDeclined,
    handleError,
  };
};
